import React, { useState } from 'react';
import {
  IonAlert,
  IonButton,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonPage,
  IonTextarea,
  IonThumbnail,
  IonToolbar,
  IonList,
  IonListHeader,
  IonIcon,
  IonFooter,
  IonGrid,
  IonRow,
  IonCol,
  IonActionSheet,
} from '@ionic/react';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { addUpdate } from '../utilities/Updates';
import { storeSubmittedNote, storeDocument } from '../utilities/Storage';
import { add } from 'ionicons/icons';
import { Toolbar } from "./Toolbar";

export default function AddNote({ history, match }) {
  const [photos, setPhotos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [noteText, setNoteText] = useState();
  const [alert, setAlert] = useState(false);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [error, setError] = useState("no error");
  const documentInputRef = React.useRef(null);

  const handleDocumentInput = (e) => {
    const files = e.target.files;
    if (files) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!documents.includes(file)) {
          setDocuments([...documents, file]);
        }
      }
    }
    e.target.value = '';
  };

  const handlePhotoFromCamera = async (source) => {
    setShowActionSheet(false);
    try {
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: source,
      });
      const resizedPhoto = await resizeImage(photo.webPath);

      // Check if photo already exists
      if (!photos.includes(resizedPhoto)) {
        setPhotos([...photos, resizedPhoto]);
      } else {
        console.warn('Photo already added');
      }
    } catch (error) {
      setError(error.message);
      console.error('Error adding photo:', error);
    }
  };

  const handleAddPhotos = async () => {   
      setShowActionSheet(true);
  };

  const handleAddDocuments = () => {
    // All platforms: open file browser for specific document types (not images)
    documentInputRef.current.accept = '.pdf,.doc,.docx,.xls,.xlsx,.txt,.ppt,.pptx,.odt,.ods,.odp';
    documentInputRef.current.multiple = true;
    documentInputRef.current.click();
  };

  const resizeImage = (imagePath) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = imagePath;

      img.onload = () => {
        let destWidth;
        let destHeight;
        const maxWH = 1000;

        if (img.width > img.height) {
          destWidth = maxWH;
          destHeight = (maxWH * img.height) / img.width;
        } else {
          destWidth = (maxWH * img.width) / img.height;
          destHeight = maxWH;
        }

        const canvas = document.createElement('canvas');
        canvas.width = destWidth;
        canvas.height = destHeight;

        const snapshotCtx = canvas.getContext('2d');
        snapshotCtx.drawImage(img, 0, 0, destWidth, destHeight);

        resolve(canvas.toDataURL('image/jpeg', 0.9));
      };
    });
  };

  const resizeImageFromDataUrl = (dataUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = dataUrl;

      img.onload = () => {
        let destWidth;
        let destHeight;
        const maxWH = 1000;

        if (img.width > img.height) {
          destWidth = maxWH;
          destHeight = (maxWH * img.height) / img.width;
        } else {
          destWidth = (maxWH * img.width) / img.height;
          destHeight = maxWH;
        }

        const canvas = document.createElement('canvas');
        canvas.width = destWidth;
        canvas.height = destHeight;

        const snapshotCtx = canvas.getContext('2d');
        snapshotCtx.drawImage(img, 0, 0, destWidth, destHeight);

        resolve(canvas.toDataURL('image/jpeg', 0.9));
      };
    });
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

  const uploadPicturesAndDocuments = async () => {
    const documentsBase64 = [];
    const documentFileNames = [];

    for (const document of documents) {
      const base64 = await toBase64(document);
      documentsBase64.push({
        name: document.name,
        base64,
      });
      documentFileNames.push(document.name);
      storeDocument(document.name, base64);
    }

    await addUpdate('add_note_to_job', {
      jobId: match.params.id,
      noteText,
      photos,
      documents: documentsBase64,
    });

    await storeSubmittedNote(match.params.id, {
      photos,
      documents: documentFileNames,
      noteText,
    });

    if (photos.length > 0) {
      setAlert(true);
    } else {
      history.goBack();
    }
  };

  return (
      <IonPage>
        <IonHeader>
          <Toolbar title="Add Note" backButtonHref="/" />
        </IonHeader>
        <IonContent>
          <IonItem>
            <IonLabel position="floating">note (mandatory) {error}</IonLabel>
            <IonTextarea
                autoGrow={true}
                onIonChange={(e) => setNoteText(e.target.value)}
            ></IonTextarea>
          </IonItem>

          <IonList lines="full">
            <IonListHeader>
              supporting photo(s)
              <IonIcon slot="end" icon={add} />
            </IonListHeader>
            {photos.length === 0 && (
                <IonItem>
                  <IonLabel>No photos added.</IonLabel>
                </IonItem>
            )}
            {photos.map((photo, index) => (
                <IonItem key={index}>
                  <IonThumbnail slot="start">
                    <img src={photo} alt="photo" />
                  </IonThumbnail>
                  <IonButton
                      fill="clear"
                      color="danger"
                      slot="end"
                      onClick={() => removePhoto(index)}
                  >
                    Remove
                  </IonButton>
                </IonItem>
            ))}
          </IonList>

          <IonList lines="full">
            <IonListHeader>
              supporting document(s)
              <IonIcon slot="end" icon={add} />
            </IonListHeader>
            {documents.length === 0 && (
                <IonItem>
                  <IonLabel>No documents added.</IonLabel>
                </IonItem>
            )}
            {documents.map((document, index) => (
                <IonItem key={index}>
                  <IonLabel>{document.name}</IonLabel>
                  <IonButton
                      fill="clear"
                      color="danger"
                      slot="end"
                      onClick={() => setDocuments(documents.filter((_, i) => i !== index))}
                  >
                    Remove
                  </IonButton>
                </IonItem>
            ))}
          </IonList>

          <input
              ref={documentInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.ppt,.pptx,.odt,.ods,.odp"
              style={{ display: 'none' }}
              onChange={handleDocumentInput}
          />
        </IonContent>

        <IonFooter>
          <IonToolbar className="ion-text-center">
            <IonGrid>
              <IonRow>
                <IonCol>
                  <IonButton
                      onClick={handleAddPhotos}
                      expand="block"
                      color="medium"
                  >
                    add photos
                  </IonButton>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol>
                  <IonButton
                      onClick={handleAddDocuments}
                      expand="block"
                      color="medium"
                  >
                    add document
                  </IonButton>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol>
                  <IonButton
                      color="medium"
                      expand="block"
                      onClick={uploadPicturesAndDocuments}
                      disabled={!noteText || noteText.length === 0}
                  >
                    submit
                  </IonButton>
                  <IonAlert
                      isOpen={alert}
                      onDidDismiss={() => history.goBack()}
                      header="success"
                      message="The note and any photos and documents will be uploaded in the background."
                      buttons={['OK']}
                  />
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonToolbar>

          <IonActionSheet
              isOpen={showActionSheet}
              onDidDismiss={() => setShowActionSheet(false)}
              header="Select Photo"
              buttons={[
                {
                  text: 'take photo',
                  icon: 'camera',
                  handler: () => handlePhotoFromCamera(CameraSource.Camera),
                },
                {
                  text: 'select from gallery',
                  icon: 'images',
                  handler: () => handlePhotoFromCamera(CameraSource.Photos),
                },
                {
                  text: 'cancel',
                  icon: 'close',
                  role: 'cancel',
                  handler: () => setShowActionSheet(false),
                },
              ]}
          />
        </IonFooter>
      </IonPage>
  );
}
